import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/*
 * Overlays for User Administration. Everything floats in a portal on <body> inside a `.ua`
 * wrapper so it picks up the page's styles without fighting Bootstrap's z-index or overflow.
 * Escape always closes the overlay that opened last.
 */

const escapeStack = [];
const onEscapeKey = (e) => {
    if (e.key !== 'Escape' || !escapeStack.length) return;
    e.preventDefault();
    e.stopPropagation();
    escapeStack[escapeStack.length - 1].current();
};

export function useEscape(handler, active = true) {
    const ref = useRef(handler);
    ref.current = handler;
    useEffect(() => {
        if (!active) return undefined;
        escapeStack.push(ref);
        if (escapeStack.length === 1) document.addEventListener('keydown', onEscapeKey, true);
        return () => {
            const i = escapeStack.indexOf(ref);
            if (i !== -1) escapeStack.splice(i, 1);
            if (!escapeStack.length) document.removeEventListener('keydown', onEscapeKey, true);
        };
    }, [active]);
}

export const Layer = ({ children }) => createPortal(<div className="ua ua-layer">{children}</div>, document.body);

/** Moves focus into a freshly opened dialog, and back to where it was when it closes. */
function useDialogFocus(ref) {
    useEffect(() => {
        const before = document.activeElement;
        const t = setTimeout(() => {
            const box = ref.current;
            if (!box) return;
            const target = box.querySelector('[data-autofocus]') || box.querySelector('input:not(:disabled), select:not(:disabled), textarea, button');
            if (target) {
                target.focus();
                if (target.readOnly && typeof target.select === 'function') target.select();
            }
        }, 30);
        return () => {
            clearTimeout(t);
            if (before && document.body.contains(before) && typeof before.focus === 'function') before.focus();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Right-hand panel for editing one user. */
export function Sheet({ onClose, labelledBy, children }) {
    const ref = useRef(null);
    useEscape(onClose);
    useDialogFocus(ref);
    return (
        <Layer>
            <div className="ua-scrim" onClick={onClose} />
            <aside ref={ref} className="ua-panel" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>{children}</aside>
        </Layer>
    );
}

export function Modal({ onClose, wide = false, title, text, footer, children }) {
    const ref = useRef(null);
    useEscape(onClose);
    useDialogFocus(ref);
    return (
        <Layer>
            <div className="ua-scrim ua-scrim-top" onClick={onClose} />
            <div ref={ref} className={`ua-modal${wide ? ' ua-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="ua-m-title">
                <div className="ua-m-head">
                    <h2 id="ua-m-title">{title}</h2>
                    {text ? <p>{text}</p> : null}
                </div>
                <div className="ua-m-body">{children}</div>
                <div className="ua-m-foot">{footer}</div>
            </div>
        </Layer>
    );
}
